import { ClickAwayListener, Grow, Paper, Popper } from "@mui/material";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { ChangeEvent, RefObject } from "react";
import type { TPeriod } from "store/periodStore";

import PeriodDropdownItem from "./PeriodDropdownItem";
import { groupPeriodsByYear } from "./utils";

interface PeriodDropdownProps {
    open: boolean;
    anchorEl: RefObject<HTMLDivElement>;
    usesMultiPeriods: boolean;
    sortedPeriods: TPeriod[];
    filteredPeriods: TPeriod[];
    currentPeriodId?: number;
    search: string;
    onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClose: () => void;
    onSelect: (id: number) => void;
    t: (key: string) => string;
}

const PeriodDropdown = ({
    open,
    anchorEl,
    usesMultiPeriods,
    sortedPeriods,
    filteredPeriods,
    currentPeriodId,
    search,
    onSearchChange,
    onClose,
    onSelect,
    t,
}: PeriodDropdownProps) => {
    const groupedPeriods = groupPeriodsByYear(filteredPeriods);

    return (
        <Popper open={open} anchorEl={anchorEl.current} placement="bottom-start" transition style={{ zIndex: 1300 }}>
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
                    <Paper elevation={0} sx={{ backgroundColor: "transparent" }}>
                        <ClickAwayListener onClickAway={onClose}>
                            <div className={`period-dropdown ${!usesMultiPeriods ? "period-dropdown--single" : ""}`}>
                                {sortedPeriods.length > 4 && (
                                    <div className="period-dropdown__search">
                                        <SmallSearchBar filter={search} onSearch={onSearchChange} borderless />
                                    </div>
                                )}

                                <div className="period-dropdown__list">
                                    {filteredPeriods.length === 0 && (
                                        <div className="period-dropdown__empty">{t("Management.NoPeriodFound")}</div>
                                    )}

                                    {[...groupedPeriods.entries()].map(([year, yearPeriods], groupIndex) => (
                                        <div key={year}>
                                            <div
                                                className={`period-dropdown__year-header ${
                                                    groupIndex === 0
                                                        ? "period-dropdown__year-header--first"
                                                        : "period-dropdown__year-header--separator"
                                                }`}
                                            >
                                                <span className="period-dropdown__year-label">{year}</span>
                                                {groupIndex > 0 && <div className="period-dropdown__year-line" />}
                                            </div>

                                            {yearPeriods.map((period) => (
                                                <PeriodDropdownItem
                                                    key={period.id}
                                                    period={period}
                                                    isSelected={period.id === currentPeriodId}
                                                    t={t}
                                                    onSelect={onSelect}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );
};

export default PeriodDropdown;
